import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AppSnackbar from "layouts/notifications/Snackbar";
import { API_BASE } from "config/api";
import { withAuthHeaders } from "services/authHeader";
import { apiFetch } from "services/apiFetch";
import { useTranslation } from "hooks/useTranslation";
import { STUDENT_SEX_OPTIONS } from "constants/studentSex";
import { normalizeStudentSex } from "utils/studentSexI18n";

/* ------------------------ helpers ------------------------ */
function resolveProgramId(student, programs) {
  const raw = student?.program_id;
  if (raw != null && raw !== "" && programs.some((p) => p.program_id === raw)) {
    return raw;
  }
  const byName = programs.find((p) => p.program_name === student?.program_name);
  if (byName) return byName.program_id;
  if (raw != null && raw !== "") {
    const byLegacyName = programs.find((p) => p.program_name === raw);
    if (byLegacyName) return byLegacyName.program_id;
  }
  return raw ?? "";
}

/* ------------------------ validation ------------------------ */
function validateForm(form, t, programs = []) {
  const errors = {};
  if (!form.name?.trim())
    errors.name = t
      ? t("students.modals.validation.nameRequired", "Name is required")
      : "Name is required";

  if (!form.sex)
    errors.sex = t
      ? t("students.modals.validation.sexRequired", "Sex is required")
      : "Sex is required";
  else if (!["male", "female"].includes(form.sex))
    errors.sex = t
      ? t("students.modals.validation.sexInvalid", "Sex must be male or female")
      : "Sex must be male or female";

  if (form.age === "" || form.age === null)
    errors.age = t
      ? t("students.modals.validation.ageRequired", "Age is required")
      : "Age is required";
  else if (!/^\d+$/.test(String(form.age)))
    errors.age = t
      ? t("students.modals.validation.ageNumber", "Age must be a number")
      : "Age must be a number";
  else if (Number(form.age) < 5 || Number(form.age) > 16)
    errors.age = t
      ? t("students.modals.validation.ageRange", "Age must be between 5 and 16")
      : "Age must be between 5 and 16";

  if (!form.program_id && form.program_id !== 0)
    errors.program_id = t
      ? t("students.modals.validation.programRequired", "Program is required")
      : "Program is required";
  else if (programs.length > 0 && !programs.some((p) => p.program_id === form.program_id)) {
    errors.program_id = t
      ? t("students.modals.validation.programInvalid", "Please select a valid program")
      : "Please select a valid program";
  }

  return errors;
}

/* -------------------------- API ----------------------------- */
async function getPrograms() {
  const url = new URL("api/programs/getPrograms", API_BASE);
  const res = await apiFetch(url, { headers: withAuthHeaders({ Accept: "application/json" }) });
  const json = await res.json();
  if (json?.status === "success") return json.data || [];
  throw new Error(json?.message || "Failed to fetch programs");
}

async function updateStudentById(studentId, form) {
  // Adjust path if your backend uses a different route
  const url = new URL("api/students/editStudentById", API_BASE);
  const payload = {
    student_id: studentId,
    name: form.name,
    sex: form.sex,
    age: Number(form.age),
    app_student_id: form.app_student_id ? String(form.app_student_id).trim() : null,
    program_id: String(form.program_id).trim(),
  };

  const res = await apiFetch(url, {
    method: "PATCH",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(payload),
  });
  return res.json();
}

/* ----------------------- Component -------------------------- */
export default function EditStudent({ open, onClose, student, onSuccess, title }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    sex: "",
    age: "",
    app_student_id: "",
    program_id: "",
  });

  const [errors, setErrors] = useState({});
  const [programs, setPrograms] = useState([]);

  const sexOptions = STUDENT_SEX_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t ? t(opt.labelKey, opt.value === "male" ? "Male" : "Female") : opt.value,
  }));

  // snackbar
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });
  const showSnackbar = (type, message) => setSnackbar({ open: true, type, message });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false, type: "", message: "" }));

  useEffect(() => {
    if (!open || !student) return;

    const load = async () => {
      try {
        const [prog] = await Promise.all([getPrograms()]);
        setPrograms(prog);

        setForm({
          name: student.name || "",
          sex: normalizeStudentSex(student.sex ?? student.gender) || "",
          age: student.age ?? "",
          app_student_id: student.app_student_id ?? "",
          program_id: resolveProgramId(student, prog),
        });
      } catch (e) {
        console.error("Failed to fetch programs/villages", e);
        showSnackbar(
          "error",
          t
            ? t("students.modals.errors.loadDropdowns", "Failed to load dropdowns")
            : "Failed to load dropdowns"
        );
      }
    };

    load();
    setErrors({});
  }, [open, student, t]);

  /* -------------------- handlers -------------------- */
  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleNumericChange = (field) => (e) => {
    const v = e.target.value;
    if (/^\d*$/.test(v)) setForm((f) => ({ ...f, [field]: v }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(form, t, programs);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    try {
      const res = await updateStudentById(student.id, form);
      if (res?.status === "success") {
        const message =
          res.message === "No changes detected"
            ? t("students.modals.success.noChanges", "No changes detected")
            : t("students.modals.success.studentUpdated", "Student details updated successfully");
        showSnackbar("success", message);
        onSuccess?.();
        onClose();
      } else {
        showSnackbar(
          "error",
          res?.message ||
            (t ? t("students.modals.errors.updateFailed", "Update failed") : "Update failed")
        );
      }
    } catch (e) {
      console.error(e);
      showSnackbar(
        "error",
        t
          ? t("students.modals.errors.serverError", "Server error during update")
          : "Server error during update"
      );
    }
  };

  const handleCancel = () => {
    onClose();
  };

  /* ------------------------ UI helpers ------------------------ */
  const renderSelect = (label, field, value, options, valueKey, labelKey) => (
    <FormControl fullWidth error={Boolean(errors[field])}>
      <InputLabel id={`${field}-label`}>{label}</InputLabel>
      <Select
        labelId={`${field}-label`}
        value={value}
        label={label}
        onChange={handleChange(field)}
        sx={{ height: 50, fontSize: "1rem" }}
      >
        {valueKey && labelKey
          ? options.map((o) => (
              <MenuItem key={o[valueKey]} value={o[valueKey]}>
                {o[labelKey]}
              </MenuItem>
            ))
          : options.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
      </Select>
      {errors[field] && (
        <p style={{ color: "red", fontSize: "0.75rem", marginTop: 4 }}>{errors[field]}</p>
      )}
    </FormControl>
  );

  const renderInput = (label, field, value, type = "text", onChange = handleChange(field)) => (
    <TextField
      label={label}
      type={type}
      fullWidth
      value={value}
      onChange={onChange}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      variant="outlined"
      sx={{ fontSize: "1rem" }}
    />
  );

  /* --------------------------- JSX --------------------------- */
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <MDBox p={3}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <MDTypography variant="h6">
              {title || (t ? t("students.modals.editStudent", "Edit Student") : "Edit Student")}
            </MDTypography>
            <IconButton onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderInput(
                t ? t("students.modals.fields.name", "Name") : "Name",
                "name",
                form.name
              )}
            </Grid>

            <Grid item xs={6}>
              {renderSelect(
                t ? t("students.modals.fields.sex", "Sex") : "Sex",
                "sex",
                form.sex,
                sexOptions,
                "value",
                "label"
              )}
            </Grid>

            <Grid item xs={6}>
              {renderInput(
                t ? t("students.modals.fields.age", "Age") : "Age",
                "age",
                form.age,
                "text",
                handleNumericChange("age")
              )}
            </Grid>

            <Grid item xs={6}>
              {renderInput(
                t ? t("students.modals.fields.appStudentId", "App Student ID") : "App Student ID",
                "app_student_id",
                form.app_student_id
              )}
            </Grid>

            <Grid item xs={6}>
              {renderSelect(
                t ? t("students.modals.fields.program", "Program") : "Program",
                "program_id",
                form.program_id,
                programs,
                "program_id",
                "program_name"
              )}
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "2px solid #218838",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  "&:hover": { backgroundColor: "#218838", borderColor: "#1e7e34" },
                  "&:active": { boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)" },
                }}
              >
                {t ? t("students.modals.buttons.save", "Save") : "Save"}
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                color="success"
                sx={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "2px solid #5a6268",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  padding: "8px 24px",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "#5a6268", borderColor: "#545b62" },
                }}
              >
                {t ? t("students.modals.buttons.cancel", "Cancel") : "Cancel"}
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </Dialog>

      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </>
  );
}

/* --------------------------- PropTypes --------------------------- */
EditStudent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  student: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    sex: PropTypes.string,
    gender: PropTypes.string,
    age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    app_student_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    program_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    program_name: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  title: PropTypes.string,
};

EditStudent.defaultProps = {
  title: null,
};
